/**
 * DemoWelcomeModal — the BETA welcome notice shown once when a visitor first
 * enters the Ando demo.
 *
 * Built on the design system's own primitives (Framer Motion + brand tokens +
 * Lucide + a portal) — the SAME headless approach as ``DemoTour`` (no generic
 * modal lib, zero AGPL/bundle/ARIA-conflict risk, fully on-brand).
 *
 * Behaviour: demo-only. Auto-opens on first demo entry and persists its
 * dismissal (per browser, via ``demoWelcomeStore``) so it never nags on
 * navigation — the same once-per-audience contract the tour uses.
 *
 * a11y: ``role="dialog"`` + ``aria-modal`` labelled/described by the copy, a
 * 40–60% scrim, focus moves to the primary CTA on open, Tab is trapped within
 * the card, Esc dismisses, and ``prefers-reduced-motion`` is honored.
 *
 * Render once in the authed shell; it no-ops unless it's a demo session AND the
 * store decided to open.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

import { cn, useReducedMotion } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Sparkles, X } from "lucide-react";

import { useDemoWelcomeStore } from "./demoWelcomeStore";
import { onboardingKey, useOnboardingMode } from "./useOnboardingMode";

export function DemoWelcomeModal(): React.ReactElement | null {
  const { i18n } = useLingui();
  const reducedMotion = useReducedMotion();
  const mode = useOnboardingMode();

  const open = useDemoWelcomeStore((s) => s.open);
  const openFor = useDemoWelcomeStore((s) => s.openFor);
  const dismiss = useDemoWelcomeStore((s) => s.dismiss);

  // Live card node tracked via a callback ref so the focus/measure effect
  // re-binds on (re)mount — same pattern as ``DemoTour``.
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);

  const isDemo = mode === "demo";

  // First demo entry → ask the store to open (no-op if already dismissed for
  // this browser). A tick after mount so the shell is painted underneath.
  useEffect(() => {
    if (!isDemo) return;
    const t = setTimeout(() => openFor(onboardingKey()), 350);
    return () => clearTimeout(t);
  }, [isDemo, openFor]);

  const close = useCallback(() => {
    dismiss(onboardingKey());
  }, [dismiss]);

  // Move focus to the primary CTA when the card mounts.
  useLayoutEffect(() => {
    if (!cardEl) return;
    cardEl.querySelector<HTMLButtonElement>("[data-welcome-primary]")?.focus();
  }, [cardEl]);

  // Esc dismisses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!isDemo || !open) return null;

  const ease = [0.32, 0.72, 0, 1] as const;

  // Minimal focus trap: keep Tab cycling within the card.
  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "Tab" || !cardEl) return;
    const focusables = cardEl.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
      {/* Scrim — 50% ink, dismisses on click (blur signals "background gone"). */}
      <motion.div
        aria-hidden
        onClick={close}
        className="absolute inset-0 bg-ando-ink/45"
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.2 }}
      />

      <AnimatePresence>
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-welcome-title"
          aria-describedby="demo-welcome-body"
          ref={setCardEl}
          onKeyDown={onCardKeyDown}
          initial={reducedMotion ? false : { opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: reducedMotion ? 0 : 0.28, ease }}
          className={cn(
            "relative z-10 w-full max-w-[440px] overflow-hidden rounded-2xl border border-hairline",
            // Pencil aesthetic: clean warm paper — NO orange gradient block.
            // The orange lives only in the mark, the pill and the primary CTA
            // (~90% neutral / ~10% accent). Depth = warm-tinted shadow-as-border,
            // not a cold drop shadow.
            "bg-card",
            "shadow-[0_0_0_1px_rgba(60,40,20,0.05),0_24px_56px_-24px_rgba(60,40,20,0.35)]",
          )}
        >
          {/* Close (soft) — the CTA is the primary path; X is the escape hatch. */}
          <button
            type="button"
            onClick={close}
            aria-label={i18n._(msg`Chiudi`)}
            className="absolute top-4 right-4 z-10 grid h-9 w-9 place-items-center rounded-full text-ink-3 transition-colors hover:bg-ando-ink/[0.06] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
          >
            <X size={16} strokeWidth={1.75} aria-hidden />
          </button>

          <div className="flex flex-col items-start gap-4 px-7 pt-8 pb-7">
            {/* Brand mark — pencil-line square, orange sparkle. No filled
                pastel chip (that reads childish); the accent is the stroke. */}
            <span
              aria-hidden
              className="grid h-10 w-10 place-items-center rounded-xl border border-ando-orange/40 bg-transparent text-ando-orange"
            >
              <Sparkles size={20} strokeWidth={1.9} />
            </span>

            {/* Beta pill — pencil line (transparent + thin orange hairline). */}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-ando-orange/45 bg-transparent px-2.5 py-1 font-mono text-[11px] text-ando-orange uppercase tracking-[0.16em]">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-ando-orange" />
              {i18n._(msg`Beta`)}
            </span>

            <h2
              id="demo-welcome-title"
              className="font-semibold text-[22px] text-ink-1 leading-tight tracking-tight"
            >
              {i18n._(msg`Benvenuto nella demo di Ando`)}
            </h2>

            <p id="demo-welcome-body" className="text-[14px] text-ink-2 leading-relaxed">
              {i18n._(
                msg`Stai per esplorare un ambiente interamente simulato, pensato per farti vivere Ando nel modo più vicino possibile alla realtà. È una beta, quindi può capitare qualche piccolo intoppo — ma riguarda solo la demo, non il prodotto: l'esperienza reale di Ando è solida e già stabile. Per qualunque cosa siamo a tua disposizione. Buona esplorazione!`,
              )}
            </p>

            <button
              data-welcome-primary
              type="button"
              onClick={close}
              className="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-ando-orange px-5 font-semibold text-[14px] text-white transition-all hover:bg-ando-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              {i18n._(msg`Inizia a esplorare`)}
              <ArrowRight size={16} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

DemoWelcomeModal.displayName = "DemoWelcomeModal";
